import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { NavBar } from "../../common/NavBar";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  getNumbersAfterEditCamera,
  getNumbersAfterSites,
} from "../EditCamera/EditCamera";
import { getAllCameras } from "../EditCamera/EditCameraUtils/getAllCameras";
import {
  createNewIgnoreZonesLinksArray,
  NewIgnoreZonesBreadCrumbs,
} from "./NewIgnoreZonesBreadCrumbs";
import { IgnoreZonePreview } from "./IgnoreZonePreview";
import { IgnoreZoneAssignmentList } from "./IgnoreZoneAssignmentList";
import { IgnoreZoneEditor } from "./IgnoreZoneEditor";
import { IgnoreZoneConfigPanel } from "./IgnoreZoneConfigPanel";
import { useNewIgnoreZonesStore } from "./newIgnoreZonesStore/newIgnoreZonesStore";

export const NewIgnoreZones = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const navigate = useNavigate();
  const siteId = Number(getNumbersAfterSites(location.href));

  const pageLoading = useNewIgnoreZonesStore((s) => s.pageLoading);
  const breadcrumbs = useNewIgnoreZonesStore((s) => s.breadcrumbs);
  const cameraName = useNewIgnoreZonesStore((s) => s.cameraName);
  const editorVisible = useNewIgnoreZonesStore((s) => s.editorVisible);
  const selectedAssignmentLocalId = useNewIgnoreZonesStore(
    (s) => s.selectedAssignmentLocalId
  );
  const getNewIgnoreZonesPageInfo = useNewIgnoreZonesStore(
    (s) => s.getNewIgnoreZonesPageInfo
  );
  const resetStore = useNewIgnoreZonesStore((s) => s.resetStore);

  const [allCamerasData, setAllCamerasData] = React.useState<any[]>([]);
  const [cameraIndex, setCameraIndex] = React.useState(0);

  const handleSitesBreadcrumbClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    navigate("/sites");
  };

  const handleSingularSiteBreadcrumbClick = () => {
    navigate(`/sites/aboutsite/${siteId}`);
  };

  const navigateToCamera = (targetCameraId: number) => {
    resetStore();
    navigate(`/sites/${siteId}/editcamera/${targetCameraId}/ignorezones`);
    getAllCameras(siteId, targetCameraId, setAllCamerasData, setCameraIndex);
    void getNewIgnoreZonesPageInfo(navigate);
  };

  React.useEffect(() => {
    resetStore();
    void getNewIgnoreZonesPageInfo(navigate);
    getAllCameras(
      siteId,
      Number(getNumbersAfterEditCamera(location.href)),
      setAllCamerasData,
      setCameraIndex
    );
    return () => {
      resetStore();
    };
  }, [getNewIgnoreZonesPageInfo, navigate, resetStore, siteId]);

  const contentLeft = navBarCollapsed ? "left-[75px]" : "left-[150px]";
  const listLeft = navBarCollapsed ? "left-[905px]" : "left-[980px]";
  const configLeft = navBarCollapsed ? "left-[970px]" : "left-[1035px]";
  const mainPanelClass = navBarCollapsed
    ? "absolute top-[145px] left-[75px] right-[1px] h-full bg-white"
    : "absolute top-[145px] left-[150px] right-[1px] h-full bg-white";

  return (
    <div>
      <BackdropLoadingScreen openState={pageLoading} />
      <div className="bg-actuate-grey absolute w-full h-full overflow-scroll">
        {!pageLoading && (
          <div>
            <div
              className={`absolute top-[15px] ${contentLeft} right-[1px] h-[59px] bg-white`}
            >
              <NewIgnoreZonesBreadCrumbs
                linksArray={createNewIgnoreZonesLinksArray(breadcrumbs)}
                cameraName={cameraName}
                handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
                handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
                styling="absolute top-[15px] left-[30px]"
              />
            </div>
            <div
              className={`absolute top-[87px] ${contentLeft} right-[1px] h-[58px] bg-white`}
            >
              <p className="font-black font-mulish text-[20px] p-3 indent-4">
                {cameraName}
              </p>
            </div>

            <div className={mainPanelClass}>
              {!editorVisible && allCamerasData.length > 1 && (
                <div className="pt-[200px] w-[40px]">
                  <Tooltip
                    title={`Previous Camera: ${
                      cameraIndex === 0
                        ? allCamerasData[allCamerasData.length - 1].camera_name
                        : allCamerasData[cameraIndex - 1].camera_name
                    }`}
                    placement="top"
                    sx={{ fontSize: "12px", fontFamily: "Mulish" }}
                  >
                    <div>
                      <OrangeTextButton
                        onClick={() => {
                          const targetCameraId =
                            cameraIndex === 0
                              ? allCamerasData[allCamerasData.length - 1].id
                              : allCamerasData[cameraIndex - 1].id;
                          navigateToCamera(targetCameraId);
                        }}
                        text={<ArrowBackIosIcon fontSize="large" id="backButton" />}
                        disabled={allCamerasData.length < 2}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}

              {!editorVisible && allCamerasData.length > 1 && (
                <div className="absolute top-[200px] left-[1250px]">
                  <Tooltip
                    title={`Next Camera: ${
                      cameraIndex + 1 === allCamerasData.length
                        ? allCamerasData[0].camera_name
                        : allCamerasData[cameraIndex + 1].camera_name
                    }`}
                    placement="top"
                    sx={{ fontSize: "12px", fontFamily: "Mulish" }}
                  >
                    <div>
                      <OrangeTextButton
                        onClick={() => {
                          const targetCameraId =
                            cameraIndex + 1 === allCamerasData.length
                              ? allCamerasData[0].id
                              : allCamerasData[cameraIndex + 1].id;
                          navigateToCamera(targetCameraId);
                        }}
                        text={
                          <ArrowForwardIosIcon fontSize="large" id="backButton" />
                        }
                        disabled={allCamerasData.length < 2}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}

              {!editorVisible && (
                <div className="absolute top-[13px] left-[70px] z-[1] flex flex-col items-start">
                  <IgnoreZonePreview />
                </div>
              )}

              <div
                className={
                  editorVisible
                    ? "player pointer-events-auto absolute top-[0px] left-[67px] z-[2] w-[775px] h-[200px] overflow-visible"
                    : "player pointer-events-none absolute top-[0px] left-[67px] z-0 w-[775px] h-[200px]"
                }
              >
                {editorVisible && (
                  <IgnoreZoneEditor key={selectedAssignmentLocalId ?? "editor"} />
                )}
              </div>
            </div>

            {!editorVisible && (
              <div className={`absolute top-[130px] ${listLeft} w-[420px]`}>
                <IgnoreZoneAssignmentList />
              </div>
            )}

            {editorVisible && (
              <div className={`absolute top-[172px] ${configLeft}`}>
                <IgnoreZoneConfigPanel />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
